/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { Button as ButtonCore, type ButtonProps as ButtonCoreProps, type ButtonRenderProps, type ContextValue, DEFAULT_SLOT, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { resolveComponentStyle } from '../../utils/resolve-component-style/resolve-component-style.util';
import { IconContext } from '../icon/icon.component';
import { NumberBadgeContext, type NumberBadgeProps } from '../number-badge/number-badge.component';
import { ProgressIndicator, ProgressIndicatorContext, type ProgressIndicatorProps } from '../progress-indicator/progress-indicator.component';
import { Text, TextContext, type TextProps } from '../text/text.component';

interface ButtonProps extends Omit<ButtonCoreProps, 'style' | 'className'> {
  /** The variant of the button. (Default: primary) */
  variant?: keyof typeof variantVariants;
  /** The size of the button. (Default: 32) */
  size?: keyof typeof sizeVariants;
  /** Whether the button is rounded. (Default: false) */
  isRounded?: boolean;
  style?: StyleOrFunction<ButtonRenderProps> | {
    base?: StyleOrFunction<ButtonRenderProps>;
    icon?: StyleOrFunction<ButtonRenderProps>;
    text?: StyleOrFunction<ButtonRenderProps>;
    numberBadge?: StyleOrFunction<ButtonRenderProps>;
  };
}

const ButtonContext = createContext<ContextValue<ButtonProps, HTMLButtonElement>>({});

const Button = forwardRef((props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, ButtonContext);

  const { style, isRounded = false, variant = 'primary', children, size = 32, ...coreProps } = props;

  return (
    <ButtonCore
      ref={ref}
      {...applyStylesWithRenderProps<ButtonRenderProps>((state) => stylex.props(
        styles.base,
        variantVariants[variant],
        sizeVariants[size],
        isRounded && styles.rounded,
        coreProps.isDisabled && styles.disabled,
        resolveComponentStyle(style, state, 'base')))}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        const contentColor = (
          renderProps.isDisabled && 'quaternary' ||
          variant === 'primary' &&
          (
            (renderProps.isHovered || renderProps.isPressed) && 'primaryHover' ||
            'white'
          ) ||
          variant === 'primaryMono' && 'inverted' ||
          variant === 'secondary' && 'black' ||
          variant === 'tertiary' && 'tertiaryHover' ||
          variant === 'quaternary' &&
          (
            (renderProps.isHovered || renderProps.isPressed) && 'primary' ||
            'secondary'
          ) ||
          variant === 'alternative' && 'black' ||
          variant === 'ghost' && (
            (renderProps.isHovered || renderProps.isPressed) && 'primary' ||
            'tertiary'
          ) ||
          'interactive'
        );

        const numberBadgeColor = (
          (renderProps.isDisabled && 'disabled') ||
          (['primary', 'primaryMono', 'tertiary'].includes(variant) && 'informational') ||
          'neutral'
        );

        return (
          <Provider
            values={[
              [TextContext, {
                style: [textStyles.base, renderProps.isPending && styles.hidden, resolveComponentStyle(style, renderProps, 'text')],
                size: TEXT_SIZES[size],
                color: renderProps.isDisabled ? 'buttonContentDisabled' : contentColor
              }],
              [ProgressIndicatorContext, {
                style: progressIndicatorStyles.base,
                size: PROGRESS_INDICATOR_SIZES[size],
                variant: (
                  (['primary', 'primaryMono'].includes(variant) && 'inverted') ||
                  (['tertiary'].includes(variant) && 'interactive') ||
                  'primary'
                )
              }],
              [IconContext, {
                slots: {
                  [DEFAULT_SLOT]: {
                    style: [iconStyles.base, renderProps.isPending && styles.hidden, resolveComponentStyle(style, renderProps, 'icon')],
                    color: contentColor,
                  },
                  [BUTTON_SLOTS.LEFT]: {
                    size: LEFT_ICON_SIZE[size],
                    color: contentColor,
                    style: [iconStyles.base, accessoryLeftStyles[size], renderProps.isPending && styles.hidden, resolveComponentStyle(style, renderProps, 'icon')],
                  },
                  [BUTTON_SLOTS.RIGHT]: {
                    size: RIGHT_ICON_SIZE[size],
                    color: contentColor,
                    style: [iconStyles.base, accessoryRightStyles[size], renderProps.isPending && styles.hidden, resolveComponentStyle(style, renderProps, 'icon')],
                  }
                }
              }],
              [NumberBadgeContext, {
                slots: {
                  [BUTTON_SLOTS.LEFT]: {
                    style: [accessoryLeftStyles[size], renderProps.isPending && styles.hidden, resolveComponentStyle(style, renderProps, 'numberBadge')],
                    size: NUMBER_BADGE_SIZES[size],
                    isPastel: false,
                    color: numberBadgeColor,
                  },
                  [BUTTON_SLOTS.RIGHT]: {
                    style: [accessoryRightStyles[size], renderProps.isPending && styles.hidden, resolveComponentStyle(style, renderProps, 'numberBadge')],
                    size: NUMBER_BADGE_SIZES[size],
                    isPastel: false,
                    color: numberBadgeColor,
                  }
                }
              }]
            ]}>
            {renderProps.isPending && <ProgressIndicator />}
            {typeof evaluatedChildren === 'string' || isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
          </Provider>
        );
      }}
    </ButtonCore>
  );
});

const TEXT_SIZES: Record<keyof typeof sizeVariants, TextProps['size']> = {
  24: 12,
  28: 12,
  32: 13,
  40: 14,
  48: 16,
  52: 16,
  64: 20,
};

const LEFT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  24: 12,
  28: 12,
  32: 16,
  40: 16,
  48: 20,
  52: 20,
  64: 24,
};

const RIGHT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  24: 12,
  28: 12,
  32: 12,
  40: 12,
  48: 16,
  52: 16,
  64: 16,
};

const PROGRESS_INDICATOR_SIZES: Record<keyof typeof sizeVariants, ProgressIndicatorProps['size']> = {
  24: 12,
  28: 12,
  32: 16,
  40: 16,
  48: 20,
  52: 20,
  64: 24,
};

const NUMBER_BADGE_SIZES: Record<keyof typeof sizeVariants, NumberBadgeProps['size']> = {
  24: 16,
  28: 16,
  32: 16,
  40: 20,
  48: 20,
  52: 24,
  64: 24,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 0,
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-pending])': 'wait',
    },
    display: 'inline-flex',
    isolation: 'isolate',
    justifyContent: 'center',
    position: 'relative',
    textDecoration: 'none',
    transitionDuration: '0.2s',
    transitionProperty: 'color, background-color, border',
    transitionTimingFunction: 'easy-in-out',
    userSelect: 'none',
  },
  rounded: { borderRadius: 999 },
  disabled: {
    backgroundColor: $.buttonSurfaceDisabled,
    borderColor: 'transparent',
  },
  hidden: { visibility: 'hidden' },
});

const variantVariants = stylex.create({
  primary: {
    backgroundColor: {
      default: $.buttonSurfacePrimary,
      ':is([data-hovered], [data-pressed])': $.buttonSurfacePrimaryHover,
    },
  },
  primaryMono: {
    backgroundColor: {
      default: $.buttonSurfacePrimaryMono,
      ':is([data-hovered], [data-pressed])': $.buttonSurfacePrimaryMonoHover,
    },
  },
  secondary: {
    backgroundColor: {
      default: $.buttonSurfaceSecondary,
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceSecondaryHover,
    },
  },
  tertiary: {
    backgroundColor: {
      default: $.buttonSurfaceTertiary,
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceTertiaryHover,
    },
  },
  quaternary: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceQuaternaryHover,
    },
    borderColor: {
      default: $.border10,
      ':is([data-hovered], [data-pressed])': 'transparent',
    },
    borderWidth: 1,
  },
  alternative: {
    backgroundColor: {
      default: $.buttonSurfaceAlternative,
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceAlternativeHover,
    },
  },
  ghost: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-pressed])': $.opacity04,
      ':is([data-pending])': $.opacity04
    }
  },
});

const sizeVariants = stylex.create({
  24: {
    borderRadius: 6,
    height: 24,
    paddingInline: 8,
  },
  28: {
    borderRadius: 6,
    height: 28,
    paddingInline: 12,
  },
  32: {
    borderRadius: 8,
    height: 32,
    paddingInline: 12,
  },
  40: {
    borderRadius: 8,
    height: 40,
    paddingInline: 16,
  },
  48: {
    borderRadius: 10,
    height: 48,
    paddingInline: 20,
  },
  52: {
    borderRadius: 10,
    height: 52,
    paddingInline: 28,
  },
  64: {
    borderRadius: 14,
    height: 64,
    paddingInline: 32,
  },
});

const textStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'stroke, fill',
    transitionTimingFunction: 'easy-in-out',
  },
});

const accessoryLeftStyles = stylex.create({
  24: { marginRight: 6 },
  28: { marginRight: 6 },
  32: { marginRight: 8 },
  40: { marginRight: 6 },
  48: { marginRight: 8 },
  52: { marginRight: 10 },
  64: { marginRight: 12 },
});

const accessoryRightStyles = stylex.create({
  24: { marginLeft: 4 },
  28: { marginLeft: 4 },
  32: { marginLeft: 4 },
  40: { marginLeft: 6 },
  48: { marginLeft: 6 },
  52: { marginLeft: 8 },
  64: { marginLeft: 8 },
});

const progressIndicatorStyles = stylex.create({ base: { position: 'absolute' } });

const BUTTON_SLOTS = {
  LEFT: 'left',
  RIGHT: 'right',
};

export { BUTTON_SLOTS, Button, ButtonContext, type ButtonProps };
